/**
 * The counter layer (D244) — the amounts a pack declares, and everything
 * that moves, gives, prints or waits on them.
 *
 * @remarks
 * The dead-content law (D31, D47) applied to amounts. Each rule refuses a
 * declaration that would load and then never do what it says:
 *
 * - **Ids are unique**, and every counter a stock, an edge or a line of
 *   prose names is declared.
 * - **An edge can be reached.** `onMin` needs something that can move the
 *   counter DOWN onto its floor (an `adjust_counter` below zero, or a
 *   `set_counter` to the floor); `onMax` something that can move it UP onto
 *   its ceiling (an adjustment above zero, a setting to the ceiling, or a
 *   stock that counts it or gives it). Otherwise the edge's effects never
 *   run.
 * - **A crossing may not cause a crossing.** An edge's effects may move only
 *   counters with no edges of their own — so the engine's fold always ends,
 *   and the order of two edges firing at once is never a question. D246:
 *   inside every arm of a branch among them too, and a move inside any arm
 *   anywhere counts toward an edge's reachability (`everyEffectOf` reads
 *   the arms).
 * - **A stock can give.** Its counted counter must start below its
 *   ceiling (a rack that starts empty never gives), and it may not also be
 *   one of the things it gives (a take would count twice).
 * - **Prose prints only declared counters**: every `{counter:<id>}` in any
 *   string of the document names one.
 *
 * One more is a WARNING (D137's channel), because it is sometimes meant:
 * **a line that prints a counter its own arm moves.** An interaction, an
 * authored verb's arm, a gift, a topic or a prompt option speaks its
 * `narratives` BEFORE its `effects` apply, and a number is printed at the
 * moment its line is said — so `SPARES {counter:counter_spares}` in the
 * lines of an arm that clips a spare prints the count before the clip.
 * When the line should read the result, it belongs in an `append_narrative`
 * after the move; when it reads what was there before (`DISCARDED: 3 MIN`),
 * leave it. D246: a move inside any arm of a branch among the effects
 * counts — every arm runs after the lines, so whichever is chosen, the line
 * has already printed the number it might change.
 *
 * And it owns the counter's half of producibility (D184's walk calls
 * {@link unproducibleCounterReadingOf}): a `counter_at_least` the pack can
 * never carry the counter onto — or, under `not`, never off — is a reading
 * with one answer.
 *
 * @packageDocumentation
 */

import {
	type ContentPackDefinition,
	type CounterAtLeastCondition,
	type CounterDefinition,
	counterIdsPrintedIn,
	type EffectDefinition,
	effectHomesWithin,
	effectsWithin,
	everyEffectOf,
} from '@orpheus/content-schema';

import { collectDuplicates } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, checkNarrativeLines, type DeclaredIdSets, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference } from './semantic-validation.js';
import { walkDocumentObjects } from './structural-walk.js';

/** How the pack can ever move one counter — read from every effect source and every stock. */
export interface CounterMoves {
	/** Some effect or stock can move it UP (an adjustment above zero, a draw that counts it, a gift). */
	readonly canRise: boolean;
	/** Some effect can move it DOWN (an adjustment below zero). */
	readonly canFall: boolean;
	/** Every value some `set_counter` puts it at. */
	readonly settings: readonly number[];
}

/** A counter nothing ever moves. */
const UNMOVED: CounterMoves = { canFall: false, canRise: false, settings: []};

/**
 * Read every way the pack moves each counter, once.
 *
 * @param contentPack - The parsed pack
 * @returns Each moved counter's moves; a counter nothing moves has no entry
 */
export const counterMovesOf = (contentPack: ContentPackDefinition): ReadonlyMap<string, CounterMoves> => {
	const moves = new Map<string, CounterMoves>();
	const record = (counterId: string, change: Partial<CounterMoves>): void => {
		const known = moves.get(counterId) ?? UNMOVED;

		moves.set(counterId, {
			canFall: known.canFall || change.canFall === true,
			canRise: known.canRise || change.canRise === true,
			settings: [...known.settings, ...(change.settings ?? [])],
		});
	};

	for (const effect of everyEffectOf(contentPack)) {
		if (effect.type === 'adjust_counter') {
			record(effect.counterId, effect.by > 0 ? { canRise: true } : { canFall: true });
		} else if (effect.type === 'set_counter') {
			record(effect.counterId, { settings: [effect.value]});
		}
	}

	// A stock's take moves its counted counter up a step and each gift up by its amount.
	for (const draw of contentPack.items.flatMap((item) => item.stock?.draws ?? [])) {
		for (const counterId of [draw.drawnCounterId, ...draw.gives.map((gift) => gift.counterId)]) {
			record(counterId, { canRise: true });
		}
	}

	return moves;
};

/**
 * Can the pack ever put this counter at `minimumCount` or above — and, the
 * other face, ever below it?
 *
 * @param counter - The declaration
 * @param moves - How the pack moves it
 * @param minimumCount - The threshold
 * @returns Both answers
 */
const reachOf = (counter: CounterDefinition, moves: CounterMoves, minimumCount: number): { readonly atLeast: boolean; readonly below: boolean } => ({
	atLeast: counter.initial >= minimumCount || moves.canRise || moves.settings.some((value) => value >= minimumCount),
	below: counter.initial < minimumCount || moves.canFall || moves.settings.some((value) => value < minimumCount),
});

/**
 * D184's question for a counter read: can the reading, with the polarity
 * the `not` wrappers above it give it, ever come out both ways?
 *
 * @remarks
 * A threshold outside the range is the reference walk's to name, and an
 * undeclared counter too — both skipped here. Otherwise the reading is
 * refused when the counter can never reach the threshold (`at least k`
 * never holds; under `not`, always does) or never fall below it (the
 * reverse) — a question with one answer either way.
 *
 * @param condition - The read
 * @param isNegated - Whether an odd number of `not` wrappers enclose it
 * @param counters - The declared counters by id
 * @param movesByCounterId - How the pack moves each counter
 * @returns The failure's message, or nothing when the reading can go both ways
 */
export const unproducibleCounterReadingOf = (
	condition: CounterAtLeastCondition,
	isNegated: boolean,
	counters: ReadonlyMap<string, CounterDefinition>,
	movesByCounterId: ReadonlyMap<string, CounterMoves>,
): string | undefined => {
	const counter = counters.get(condition.counterId);

	if (counter === undefined || condition.minimumCount <= counter.min || condition.minimumCount > counter.max) {
		return undefined;
	}

	const reach = reachOf(counter, movesByCounterId.get(condition.counterId) ?? UNMOVED, condition.minimumCount);
	const threshold = `"at least ${String(condition.minimumCount)}"`;

	if (!reach.atLeast) {
		return `the counter starts at ${String(counter.initial)} and nothing moves it up to ${String(condition.minimumCount)}, so ${threshold} never holds — ${isNegated ? 'under `not`, this condition ALWAYS holds, and whatever it guards shadows everything after it' : 'this condition can never hold, and whatever it guards is never reached'}`;
	}

	if (!reach.below) {
		return `the counter starts at ${String(counter.initial)} and nothing moves it below ${String(condition.minimumCount)}, so ${threshold} always holds — ${isNegated ? 'under `not`, this condition can never hold, and whatever it guards is never reached' : 'this condition ALWAYS holds, and whatever it guards shadows everything after it'}`;
	}

	return undefined;
};

/**
 * One counter's edges: their effects walked, reachable, and never causing another crossing.
 *
 * @param counter - The declaration
 * @param counterPath - Its path
 * @param moves - How the pack moves it
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkCounterEdges = (
	counter: CounterDefinition,
	counterPath: string,
	moves: CounterMoves,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	const edges = [
		{ effects: counter.onMin, field: 'onMin', isReachable: moves.canFall || moves.settings.includes(counter.min), way: 'down onto its floor' },
		{ effects: counter.onMax, field: 'onMax', isReachable: moves.canRise || moves.settings.includes(counter.max), way: 'up onto its ceiling' },
	];

	for (const edge of edges) {
		if (edge.effects.length > 0 && !edge.isReachable) {
			failures.push(brokenReference(`${counterPath}.${edge.field}`, counter.counterId, `nothing in the pack moves the counter ${edge.way}, so these effects never run`));
		}

		// D246: a move inside any arm of a branch at the edge is a move from the edge.
		for (const [effectsPath, effects] of effectHomesWithin([`${counterPath}.${edge.field}`, edge.effects])) {
			effects.forEach((effect, effectIndex) => {
				if (effect.type !== 'adjust_counter' && effect.type !== 'set_counter') {
					return;
				}

				const moved = declared.counters.get(effect.counterId);

				if (moved !== undefined && moved.onMin.length + moved.onMax.length > 0) {
					failures.push(
						brokenReference(
							`${effectsPath}[${String(effectIndex)}]`,
							effect.counterId,
							`a crossing may not cause a crossing — this counter has edge effects of its own, so moving it from ${counter.counterId}'s ${edge.field} could fold without end; move it from somewhere else`,
						),
					);
				}
			});
		}
	}
};

/**
 * Every stock: its counters declared, able to give, never counting itself, its words and effects walked.
 *
 * @param contentPack - The parsed pack
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkStocks = (contentPack: ContentPackDefinition, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	contentPack.items.forEach((item, itemIndex) => {
		item.stock?.draws.forEach((draw, drawIndex) => {
			const drawPath = `items[${String(itemIndex)}].stock.draws[${String(drawIndex)}]`;
			const drawn = declared.counters.get(draw.drawnCounterId);

			if (drawn === undefined) {
				failures.push(brokenReference(`${drawPath}.drawnCounterId`, draw.drawnCounterId, 'the stock counts an undeclared counter'));
			} else if (drawn.initial === drawn.max) {
				failures.push(brokenReference(`${drawPath}.drawnCounterId`, draw.drawnCounterId, 'the counter starts at its ceiling, so the stock is empty from the start and never gives'));
			}

			draw.gives.forEach((gift, giftIndex) => {
				const giftPath = `${drawPath}.gives[${String(giftIndex)}].counterId`;

				if (!declared.counters.has(gift.counterId)) {
					failures.push(brokenReference(giftPath, gift.counterId, 'the stock gives to an undeclared counter'));
				} else if (gift.counterId === draw.drawnCounterId) {
					failures.push(brokenReference(giftPath, gift.counterId, 'the stock gives to the counter it counts, so every take would count twice'));
				}
			});

			if (draw.when !== undefined) {
				checkCondition(draw.when, `${drawPath}.when`, declared, failures);
			}

			checkNarrativeLines(draw.takeNarratives, `${drawPath}.takeNarratives`, declared, failures);
			checkNarrativeLines(draw.emptyNarratives, `${drawPath}.emptyNarratives`, declared, failures);
			checkNarrativeLines(draw.fullNarratives, `${drawPath}.fullNarratives`, declared, failures);
		});
	});
};

/**
 * Every string in the document, with its path — the prose a `{counter:…}` token can hide in.
 *
 * @param node - The document, or a part of it
 * @param path - The part's path
 * @param visit - Called once per string
 */
const walkDocumentStrings = (node: unknown, path: string, visit: (text: string, textPath: string) => void): void => {
	if (typeof node === 'string') {
		visit(node, path);
	} else if (Array.isArray(node)) {
		node.forEach((element, elementIndex) => {
			walkDocumentStrings(element, `${path}[${String(elementIndex)}]`, visit);
		});
	} else if (typeof node === 'object' && node !== null) {
		for (const [key, value] of Object.entries(node)) {
			walkDocumentStrings(value, path === '' ? key : `${path}.${key}`, visit);
		}
	}
};

/**
 * Validate the pack's counters.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateCounterAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);
	const movesByCounterId = counterMovesOf(contentPack);

	collectDuplicates(
		contentPack.counters.map((counter, counterIndex) => ({ declarationPath: `counters[${String(counterIndex)}].counterId`, declaredId: counter.counterId })),
		'counter',
		failures,
	);
	contentPack.counters.forEach((counter, counterIndex) => {
		checkCounterEdges(counter, `counters[${String(counterIndex)}]`, movesByCounterId.get(counter.counterId) ?? UNMOVED, declared, failures);
	});
	checkStocks(contentPack, declared, failures);
	walkDocumentStrings(contentPack, '', (text, textPath) => {
		for (const counterId of counterIdsPrintedIn(text)) {
			if (!declared.counters.has(counterId)) {
				failures.push(brokenReference(textPath, counterId, `the prose prints {counter:${counterId}}, and no counter by that id is declared`));
			}
		}
	});

	return failures;
};

/** A line printing a counter that the same arm's effects move afterward. */
export interface CounterPrintedBeforeMove {
	readonly counterId: string;
	readonly path: string;
}

/**
 * Every arm whose spoken lines print a counter its own effects then move —
 * found by shape (an object carrying both `narratives` and `effects`), so a
 * new arm family is covered the day it is written.
 *
 * @param contentPack - The parsed pack
 * @returns One finding per arm and counter, in document order
 */
export const findCountersPrintedBeforeMoved = (contentPack: ContentPackDefinition): readonly CounterPrintedBeforeMove[] => {
	const findings: CounterPrintedBeforeMove[] = [];

	walkDocumentObjects(contentPack, (record, path) => {
		const { effects, narratives } = record;

		if (!Array.isArray(effects) || !Array.isArray(narratives)) {
			return;
		}

		// The walk hands back the parsed pack's own objects, so an arm's `effects` is an effect list.
		const moved = new Set(
			effectsWithin(effects as readonly EffectDefinition[]).flatMap((effect) => (effect.type === 'adjust_counter' || effect.type === 'set_counter' ? [effect.counterId as string] : [])),
		);
		const printed = new Set<string>();

		walkDocumentStrings(narratives, '', (text) => {
			for (const counterId of counterIdsPrintedIn(text)) {
				printed.add(counterId);
			}
		});

		for (const counterId of printed) {
			if (moved.has(counterId)) {
				findings.push({ counterId, path: `${path}.narratives` });
			}
		}
	});

	return findings;
};
